import { useState, useEffect, useCallback, useMemo } from 'react';
import apiClient from '@/api/client';

/**
 * useColumnSettings — hook for managing table column visibility and order.
 *
 * Loads settings from backend (MongoDB), falls back to localStorage,
 * and syncs changes to both.
 *
 * @param {string} pageId — unique page identifier for settings storage
 * @param {Array} allColumns — all available columns [{key, label, ...}]
 * @returns {Object} — settings state and actions
 */
export function useColumnSettings(pageId, allColumns) {
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() =>
    allColumns.map(col => col.key)
  );
  const [columnOrder, setColumnOrder] = useState(() =>
    allColumns.map(col => col.key)
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // LocalStorage key for fallback
  const localStorageKey = `column-settings-${pageId}`;

  // Load settings on mount
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user-preferences/columns/${pageId}`);

      if (response.data) {
        const { visible_columns, column_order } = response.data;
        if (visible_columns?.length > 0) {
          setVisibleColumnKeys(visible_columns);
        }
        if (column_order?.length > 0) {
          setColumnOrder(column_order);
        }
        // Sync to localStorage
        localStorage.setItem(localStorageKey, JSON.stringify({
          visible_columns,
          column_order
        }));
      } else {
        // No backend settings, try localStorage
        const cached = localStorage.getItem(localStorageKey);
        if (cached) {
          const { visible_columns, column_order } = JSON.parse(cached);
          if (visible_columns?.length > 0) {
            setVisibleColumnKeys(visible_columns);
          }
          if (column_order?.length > 0) {
            setColumnOrder(column_order);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load column settings from backend, using localStorage', error);
      // Fallback to localStorage
      const cached = localStorage.getItem(localStorageKey);
      if (cached) {
        try {
          const { visible_columns, column_order } = JSON.parse(cached);
          if (visible_columns?.length > 0) {
            setVisibleColumnKeys(visible_columns);
          }
          if (column_order?.length > 0) {
            setColumnOrder(column_order);
          }
        } catch (e) {
          console.warn('Failed to parse localStorage cache', e);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [pageId, localStorageKey]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = useCallback(async (newVisibleKeys, newOrder) => {
    try {
      setSaving(true);
      await apiClient.put(`/user-preferences/columns/${pageId}`, {
        visible_columns: newVisibleKeys,
        column_order: newOrder
      });
      // Update localStorage
      localStorage.setItem(localStorageKey, JSON.stringify({
        visible_columns: newVisibleKeys,
        column_order: newOrder
      }));
    } catch (error) {
      console.error('Failed to save column settings', error);
      // Still update localStorage as fallback
      localStorage.setItem(localStorageKey, JSON.stringify({
        visible_columns: newVisibleKeys,
        column_order: newOrder
      }));
    } finally {
      setSaving(false);
    }
  }, [pageId, localStorageKey]);

  const resetSettings = useCallback(async () => {
    try {
      setSaving(true);
      await apiClient.delete(`/user-preferences/columns/${pageId}`);
      localStorage.removeItem(localStorageKey);
      // Reset to defaults
      const defaultKeys = allColumns.map(col => col.key);
      setVisibleColumnKeys(defaultKeys);
      setColumnOrder(defaultKeys);
    } catch (error) {
      console.error('Failed to reset column settings', error);
      // Reset locally anyway
      localStorage.removeItem(localStorageKey);
      const defaultKeys = allColumns.map(col => col.key);
      setVisibleColumnKeys(defaultKeys);
      setColumnOrder(defaultKeys);
    } finally {
      setSaving(false);
    }
  }, [pageId, localStorageKey, allColumns]);

  // Toggle column visibility
  const toggleColumn = useCallback((key) => {
    setVisibleColumnKeys(prev => {
      const newKeys = prev.includes(key)
        ? prev.filter(k => k !== key)
        : [...prev, key];
      return newKeys;
    });
  }, []);

  // Set visible columns (bulk)
  const setVisibleColumns = useCallback((keys) => {
    setVisibleColumnKeys(keys);
  }, []);

  // Reorder columns by key (move fromKey to position of toKey)
  const reorderColumns = useCallback((fromKey, toKey) => {
    setColumnOrder(prev => {
      const newOrder = [...prev];
      const fromIndex = newOrder.indexOf(fromKey);
      const toIndex = newOrder.indexOf(toKey);
      if (fromIndex === -1 || toIndex === -1) return prev;
      const [removed] = newOrder.splice(fromIndex, 1);
      newOrder.splice(toIndex, 0, removed);
      return newOrder;
    });
  }, []);

  // Apply current settings (save to backend)
  const applySettings = useCallback(() => {
    saveSettings(visibleColumnKeys, columnOrder);
  }, [visibleColumnKeys, columnOrder, saveSettings]);

  // Get columns in order with visibility applied
  const getOrderedColumns = useCallback((columns, roleFilter = null) => {
    // First filter by role if provided
    let filteredColumns = roleFilter
      ? columns.filter(roleFilter)
      : columns;

    // Create a map for quick lookup
    const columnMap = new Map(filteredColumns.map(col => [col.key, col]));

    // Order columns by columnOrder, filtering only visible ones
    const ordered = [];
    for (const key of columnOrder) {
      if (visibleColumnKeys.includes(key) && columnMap.has(key)) {
        ordered.push(columnMap.get(key));
      }
    }

    // Add any columns not in columnOrder (new columns added later)
    for (const col of filteredColumns) {
      if (!columnOrder.includes(col.key) && visibleColumnKeys.includes(col.key)) {
        ordered.push(col);
      }
    }

    return ordered;
  }, [columnOrder, visibleColumnKeys]);

  // Check if settings have been customized
  const isCustomized = useMemo(() => {
    const defaultKeys = allColumns.map(col => col.key);
    const visibleChanged = visibleColumnKeys.length !== defaultKeys.length ||
      !visibleColumnKeys.every(key => defaultKeys.includes(key));
    const orderChanged = !columnOrder.every((key, idx) => key === defaultKeys[idx]);
    return visibleChanged || orderChanged;
  }, [allColumns, visibleColumnKeys, columnOrder]);

  return {
    // State
    visibleColumnKeys,
    columnOrder,
    loading,
    saving,
    isCustomized,

    // Actions
    toggleColumn,
    setVisibleColumns,
    reorderColumns,
    applySettings,
    resetSettings,
    getOrderedColumns,
  };
}
